import { useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';
import { Delta } from 'quill/core';
import React, { useState } from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import Avatar from '../../components/Avatar';
import Button from '../../components/Button';
import { MenuItem } from '../../components/Menu';
import { UIActions } from '../../ui/uiSlice';
import getUserNames from '../../user/getUserNames';
import useNavigate from '../../utils/router/useNavigate';
import postPath from '../../utils/url/postPath';
import PostIcon from '../PostIcon';
import * as styles from './HistoryRow.module.scss';
import OverflowMenu from '../../components/OverflowMenu';
import formatHistoryDate from './formatHistoryDate';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import { useGetUser } from '../../user/useAllUsers';

const HISTORY_ROW_AUTHORS_FRAGMENT = gql(`
  fragment HistoryRow_authors on User {
    id
    ...useGetUser_user
  }
`);

const UPDATE_POST_CONTENT = gql(`
  mutation updatePostContent(
    $id: ID!
    $delta: Json!
    $version: Int!
    $checksum: Int!
  ) {
    post: updatePostContent(
      id: $id
      delta: $delta
      version: $version
      checksum: $checksum
    ) {
      id
    }
  }
`);

const POST_CONTENT_QUERY = gql(`
  query PostHistoryContent($postId: ID!, $versionFrom: Int!, $versionTo: Int!) {
    post(id: $postId) {
      id
      content
      version
      ...postPath_post
      history(versionFrom: $versionFrom, versionTo: $versionTo) {
        id
        snapshot
        delta
      }
    }
  }
`);

interface HistoryRowProp {
  active: boolean;
  authors?: DocumentType<typeof HISTORY_ROW_AUTHORS_FRAGMENT>[] | null;
  postId: string;
  versionFrom: number;
  versionTo: number;
  editedAt: string;
  isCurrent: boolean;
  canRestore: boolean;
  onClick: (e: React.MouseEvent) => void;
}

const HistoryRow = ({
  active,
  authors,
  postId,
  editedAt,
  versionFrom,
  versionTo,
  isCurrent,
  canRestore,
  onClick,
}: HistoryRowProp) => {
  const getUser = useGetUser();
  const fullAuthors = authors?.map(getUser) ?? [];
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { refetch: getPostContent } = useQuery(POST_CONTENT_QUERY, {
    skip: true,
    fetchPolicy: 'network-only',
    variables: { postId, versionFrom, versionTo },
  });

  const [updatePostContent] = useMutation(UPDATE_POST_CONTENT);

  return (
    <div
      className={classNames(styles.container, {
        [styles.isMenuOpen]: isMenuOpen,
      })}
    >
      <Button
        className={classNames(styles.button, { [styles.active]: active })}
        onClick={onClick}
      >
        {fullAuthors?.[0] ? (
          <Avatar user={fullAuthors[0]} />
        ) : (
          <div className={styles.postIconWrapper}>
            <PostIcon />
          </div>
        )}
        <div className={styles.description}>
          {fullAuthors && (
            <div className={styles.authors}>
              {getUserNames(fullAuthors, { limit: 2 })}
            </div>
          )}
          {isCurrent ? (
            'Currently editing'
          ) : (
            <div className={styles.timestamp}>
              {formatHistoryDate(editedAt)}
            </div>
          )}
        </div>
      </Button>
      <div className={styles.menuContainer}>
        {canRestore && (
          <OverflowMenu
            applyNegativeMargin
            iconSize="regular"
            open={isMenuOpen}
            onOpenChange={setIsMenuOpen}
          >
            <MenuItem
              onClick={() => {
                async function restore() {
                  const {
                    data: { post },
                  } = await getPostContent();
                  if (!post.history) return;

                  const currentDelta = new Delta(post.content);
                  const targetDelta = new Delta(post.history.snapshot).compose(
                    new Delta(post.history.delta),
                  );

                  const diff = currentDelta.diff(targetDelta);

                  await updatePostContent({
                    variables: {
                      id: postId,
                      delta: JSON.stringify(diff),
                      version: post.version,
                      checksum: currentDelta.length(),
                    },
                  });
                  navigate(postPath(post));
                  dispatch(
                    UIActions.pushToast({
                      level: 'success',
                      message: 'Restored your post!',
                    }),
                  );
                }

                restore();
              }}
            >
              Restore this version
            </MenuItem>
          </OverflowMenu>
        )}
      </div>
    </div>
  );
};

export default HistoryRow;
